import { safeImageUrl } from '../../../renderer/url-safety';
import type { DecorConfig } from '../manifest';
import { useScene } from '../scene-context';

type Props = {
  decor?: DecorConfig;
  variant?: 'hero' | 'section';
};

export function DecorLayer({ decor, variant = 'section' }: Props) {
  const { pack } = useScene();
  if (!decor) return null;

  const cornerUrl = safeImageUrl(decor.corner?.url);
  const deityUrl = safeImageUrl(decor.deity?.url);
  const dividerUrl = safeImageUrl(decor.divider?.url);
  const isHero = variant === 'hero';
  const opacity = decor.intensity === 'ceremonial' ? 0.95 : decor.intensity === 'ornate' ? 0.72 : 0.46;

  return (
    <div className="pointer-events-none absolute inset-0 z-[6] overflow-hidden" aria-hidden>
      <style>{`
        @keyframes ic-foil-drift {
          0% { transform: translate3d(0, 0, 0) rotate(0deg); filter: brightness(1); }
          50% { filter: brightness(1.16); }
          100% { transform: translate3d(0, -8px, 0) rotate(1.2deg); filter: brightness(1.03); }
        }
        @keyframes ic-line-sheen {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}</style>

      {pack.id === 'heritage' ? (
        <div className="absolute inset-4 overflow-hidden rounded-[2rem]">
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ opacity: opacity * 0.95 }}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            {/* Scalloped Mewar Rajput Arch */}
            <path
              d="M 5,95 L 5,25 Q 5,12 18,12 Q 22,12 28,15 Q 34,18 40,13 Q 45,8 50,8 Q 55,8 60,13 Q 66,18 72,15 Q 78,12 82,12 Q 95,12 95,25 L 95,95 Z"
              fill="none"
              stroke={pack.colors.primary}
              strokeWidth="0.8"
              strokeDasharray="1.5 0.75"
            />
            <path
              d="M 6.5,93.5 L 6.5,26 Q 6.5,14 18,14 Q 21.5,14 27.5,17 Q 33.5,20 39.5,15 Q 45,10 50,10 Q 55,10 60,15 Q 66.5,20 72.5,17 Q 78.5,14 82,14 Q 93.5,14 93.5,26 L 93.5,93.5 Z"
              fill="none"
              stroke={pack.colors.secondary}
              strokeWidth="0.4"
            />
          </svg>
          {/* Subtle gold floral motifs in bottom corners */}
          <div className="absolute bottom-6 left-6 text-xs uppercase tracking-widest opacity-80" style={{ color: pack.colors.primary, fontFamily: pack.fonts.body }}>⚜</div>
          <div className="absolute bottom-6 right-6 text-xs uppercase tracking-widest opacity-80" style={{ color: pack.colors.primary, fontFamily: pack.fonts.body }}>⚜</div>
        </div>
      ) : (
        <div
          className="absolute inset-x-4 top-4 bottom-4 rounded-[2rem] border"
          style={{
            borderColor: `${pack.colors.primary}${decor.intensity === 'ceremonial' ? '80' : '45'}`,
            boxShadow: `inset 0 0 0 1px ${pack.colors.text}18, inset 0 0 48px ${pack.colors.primary}22`,
            opacity,
          }}
        />
      )}
      <div
        className="absolute left-1/2 top-8 h-px w-[min(72vw,820px)] -translate-x-1/2"
        style={{
          background: `linear-gradient(90deg, transparent, ${pack.colors.primary}, ${pack.colors.text}, ${pack.colors.primary}, transparent)`,
          backgroundSize: '200% 100%',
          animation: 'ic-line-sheen 7s linear infinite',
        }}
      />
      <div
        className="absolute bottom-8 left-1/2 h-px w-[min(62vw,720px)] -translate-x-1/2"
        style={{
          background: `linear-gradient(90deg, transparent, ${pack.colors.accent}, ${pack.colors.primary}, transparent)`,
          backgroundSize: '200% 100%',
          animation: 'ic-line-sheen 9s linear infinite reverse',
        }}
      />

      {cornerUrl && (
        <>
          <img
            src={cornerUrl}
            alt=""
            className="absolute -left-8 -top-6 w-[min(42vw,260px)] max-w-none object-contain"
            style={{ opacity, animation: 'ic-foil-drift 10s ease-in-out infinite alternate' }}
          />
          <img
            src={cornerUrl}
            alt=""
            className="absolute -right-8 -bottom-6 w-[min(42vw,260px)] max-w-none rotate-180 object-contain"
            style={{ opacity, animation: 'ic-foil-drift 11s ease-in-out infinite alternate-reverse' }}
          />
        </>
      )}

      {deityUrl && isHero && (
        <img
          src={deityUrl}
          alt={decor.deity?.alt ?? ''}
          className="absolute left-1/2 top-[8vh] w-[min(44vw,230px)] -translate-x-1/2 object-contain mix-blend-screen"
          style={{ opacity: Math.min(1, opacity + 0.15), filter: `drop-shadow(0 0 24px ${pack.colors.primary}66)` }}
        />
      )}

      {pack.id === 'heritage' && isHero && !deityUrl && (
        <div className="absolute top-[8.5vh] left-1/2 -translate-x-1/2 flex flex-col items-center z-20">
          <svg
            className="w-14 h-14"
            viewBox="0 0 100 100"
            fill="none"
            stroke={pack.colors.primary}
            strokeWidth="1.5"
          >
            {/* Elegant Ganesha SVG Line Art */}
            <path d="M 50,15 C 45,15 42,20 42,28 C 42,32 45,35 50,38 C 55,35 58,32 58,28 C 58,20 55,15 50,15 Z" />
            <path d="M 50,8 L 50,15" />
            <path d="M 42,22 C 34,22 32,32 42,32" />
            <path d="M 58,22 C 66,22 68,32 58,32" />
            <path d="M 50,38 C 48,46 44,52 48,56 C 50,58 54,58 55,54 C 55,50 51,46 50,44" />
            <path d="M 44,35 L 40,36" />
            <circle cx="56" cy="46" r="2" fill={pack.colors.primary} />
          </svg>
          <div
            className="text-[9px] tracking-[0.3em] uppercase mt-1 font-semibold"
            style={{ color: pack.colors.primary, fontFamily: pack.fonts.body }}
          >
            शुभ विवाह
          </div>
        </div>
      )}

      {dividerUrl && !isHero && (
        <img
          src={dividerUrl}
          alt={decor.divider?.alt ?? ''}
          className="absolute left-1/2 top-[13vh] w-[min(44vw,280px)] -translate-x-1/2 object-contain"
          style={{ opacity: Math.min(0.55, opacity) }}
        />
      )}
    </div>
  );
}
